import { type CalendarDate, type Time, today } from '@internationalized/date';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../atoms/Button/Button';
import DatePickerShell from '../shared/DatePickerShell/DatePickerShell';
import { formatDisplayValue, TIME_ZONE, toJsDate } from '../shared/dateUtils';
import { usePopoverDateValue } from '../shared/usePopoverDateValue';
/**
 * DatePickerActions — the **transactional** popover date picker: a read-only `InputText` trigger opens a popover
 * with react-aria's `Calendar` (+ optional `TimeField`), a generic `extraActions` slot, and a Clear/Cancel/Save
 * button bar. Unlike `DatePicker` (live commit), it owns the whole edit→commit/cancel lifecycle internally:
 *
 * - The trigger shows the **committed** value, which only changes on Save (or Clear). Editing the calendar/time
 *   only touches an internal draft, so the field never moves until the user confirms.
 * - Cancel / Escape / outside click discard the draft (the next open reseeds it from the committed value).
 * - On Save it emits `onSave({ value, extraActions })`; the consumer just persists. There is no `onChange` and no
 *   `onCancel`, so consumers hold no draft state of their own.
 *
 * `extraActions` is a generic, component-managed draft slot for extra controls (e.g. a deliver-late checkbox):
 * the component seeds it from `initial`, hands `{ value, setValue }` to `render`, reverts it with the date, and
 * returns it in `onSave`. Use an object value for several controls. `initial` should be a primitive or a stable
 * reference (it is compared by identity to adopt external changes while the popover is closed).
 *
 * Public contract uses plain JS `Date`. All `@internationalized/date` conversion lives in `../shared/dateUtils`.
 */

type ExtraActions<TExtra> = {
  initial: TExtra;
  render: (api: { setValue: (next: TExtra) => void; value: TExtra }) => React.ReactNode;
};

type ActionsButtonsProps = {
  buttonTextCancel: string;
  buttonTextClear: string;
  buttonTextSuccess: string;
  isDisabledSuccess?: boolean;
  onCancel: () => void;
  onClear?: () => void;
  onSuccess: () => void;
};

// Clear/Cancel/Save bar (Clear sits on the left, mirroring the v2 Dialog's customFooterLeft).
function ActionsButtons({
  buttonTextCancel,
  buttonTextClear,
  buttonTextSuccess,
  isDisabledSuccess = false,
  onCancel,
  onClear,
  onSuccess,
}: ActionsButtonsProps): JSX.Element {
  return (
    <div className="date-picker-popup__buttons">
      {onClear && <Button className="date-picker-popup__button-clear" onClick={onClear} size="sm" text={buttonTextClear} variant="secondary-ghost" />}
      <Button onClick={onCancel} size="sm" text={buttonTextCancel} variant="secondary-ghost" />
      <Button isDisabled={isDisabledSuccess} onClick={onSuccess} size="sm" text={buttonTextSuccess} />
    </div>
  );
}

export type DatePickerActionsProps<TExtra> = {
  buttonTextCancel?: string;
  buttonTextClear?: string;
  buttonTextSuccess?: string;
  disabled?: boolean;
  extraActions?: ExtraActions<TExtra>;
  // Shows a native year <select> in the calendar header (month stays on prev/next).
  hasYearPicker?: boolean;
  isDisabledSuccess?: boolean;
  isLabelHidden?: boolean;
  isNonModal?: boolean;
  label?: string;
  onClear?: () => void;
  onSave: (payload: { extraActions: TExtra | undefined; value: Date | null }) => void;
  placeholder?: string;
  shouldDisablePast?: boolean;
  showHour?: boolean;
  value?: Date | string | null;
};

function DatePickerActions<TExtra = undefined>({
  buttonTextCancel,
  buttonTextClear,
  buttonTextSuccess,
  disabled = false,
  extraActions,
  hasYearPicker = false,
  isDisabledSuccess = false,
  isLabelHidden = false,
  isNonModal = false,
  label,
  onClear,
  onSave,
  placeholder,
  shouldDisablePast = true,
  showHour = false,
  value = null,
}: DatePickerActionsProps<TExtra>): JSX.Element {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const draftDateState = usePopoverDateValue(toJsDate(value));

  // The committed value the trigger displays; it only changes on Save/Clear (editing touches the draft below).
  const [committedDateValue, setCommittedDateValue] = React.useState<Date | null>(() => toJsDate(value));
  // Draft and committed state for the generic extra slot, mirroring the date.
  const [draftExtraActionsValue, setDraftExtraActionsValue] = React.useState<TExtra | undefined>(extraActions?.initial);
  const [committedExtraActionsValue, setCommittedExtraActionsValue] = React.useState<TExtra | undefined>(extraActions?.initial);

  // Keep the internal state in sync when the consumer changes the `value` prop from outside (async load,
  // post-save refresh, reusing the instance for another item…) — the useState seeds only run at mount, so
  // without this the field would stay stale. The draft always re-seeds; the committed (displayed) value only
  // adopts the change while closed, so an external update never makes the field jump while the user is editing.
  const [prevDateValue, setPrevDateValue] = React.useState<Date | string | null | undefined>(value);
  if (value !== prevDateValue) {
    setPrevDateValue(value);
    const next = toJsDate(value);
    draftDateState.setFromJsDate(next);
    if (!isOpen) setCommittedDateValue(next);
  }

  // Same for the extra slot. `initial` is compared by identity, so it must be a primitive or a stable reference.
  const initialExtraActionsValue = extraActions?.initial;
  const [prevExtraActionsValue, setPrevExtraActionsValue] = React.useState<TExtra | undefined>(initialExtraActionsValue);
  if (initialExtraActionsValue !== prevExtraActionsValue) {
    setPrevExtraActionsValue(initialExtraActionsValue);
    setDraftExtraActionsValue(initialExtraActionsValue);
    if (!isOpen) setCommittedExtraActionsValue(initialExtraActionsValue);
  }

  const minValue = shouldDisablePast ? today(TIME_ZONE) : undefined;

  function handleDateChange(nextDate: CalendarDate): void {
    draftDateState.changeDate(nextDate);
  }

  function handleTimeChange(nextTime: Time | null): void {
    draftDateState.changeTime(nextTime);
  }

  // Reseed the draft (date + extra actions) from the committed values, discarding any abandoned edit.
  function reseedDraft(): void {
    draftDateState.setFromJsDate(committedDateValue);
    setDraftExtraActionsValue(committedExtraActionsValue);
  }

  function open(): void {
    if (disabled) return;
    reseedDraft();
    setIsOpen(true);
  }

  function close(): void {
    setIsOpen(false);
  }

  function handleSave(): void {
    setCommittedDateValue(draftDateState.jsDate);
    setCommittedExtraActionsValue(draftExtraActionsValue);
    onSave({ extraActions: draftExtraActionsValue, value: draftDateState.jsDate });
    close();
  }

  function handleClear(): void {
    setCommittedDateValue(null);
    onClear?.();
    close();
  }

  // react-aria's Popover reports open and close through one boolean callback. We reseed the draft on every
  // transition — on open it starts a fresh edit from the committed values, on close (Cancel / Escape / outside
  // click) it discards the abandoned draft (harmless while closed, since the draft isn't read) — then mirror the
  // value into state, gating on `disabled`. (No branch: setIsOpen takes the value.)
  function handleOpenChange(nextOpen: boolean): void {
    reseedDraft();
    setIsOpen(nextOpen && !disabled);
  }

  const accessibleName = label || placeholder || t('common:Select date');

  return (
    <DatePickerShell
      accessibleName={accessibleName}
      actionsElement={
        <ActionsButtons
          buttonTextCancel={buttonTextCancel ?? t('actions:Cancel')}
          buttonTextClear={buttonTextClear ?? t('actions:Clear date')}
          buttonTextSuccess={buttonTextSuccess ?? t('actions:Save')}
          isDisabledSuccess={isDisabledSuccess}
          onCancel={close}
          onClear={onClear ? handleClear : undefined}
          onSuccess={handleSave}
        />
      }
      dateValue={draftDateState.dateValue}
      disabled={disabled}
      displayValue={formatDisplayValue(committedDateValue, showHour, i18n.language)}
      extraActions={extraActions?.render({ setValue: setDraftExtraActionsValue, value: draftExtraActionsValue })}
      hasYearPicker={hasYearPicker}
      isLabelHidden={isLabelHidden}
      isNonModal={isNonModal}
      isOpen={isOpen}
      label={label}
      locale={i18n.language}
      minValue={minValue}
      onDateChange={handleDateChange}
      onOpen={open}
      onOpenChange={handleOpenChange}
      onTimeChange={handleTimeChange}
      placeholder={placeholder || t('common:Select date')}
      showHour={showHour}
      timeValue={draftDateState.timeValue}
    />
  );
}

export default DatePickerActions;
